import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MagnifyingGlass, Buildings, GraduationCap, UsersThree, Users, Briefcase, UserPlus,
  Flower, Certificate, Coins, ChatsCircle, Newspaper, ShieldCheck, Brain, Translate,
  Headphones, LockKey, CaretDown, CaretRight, MapPin, CalendarBlank, BookOpen,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useLanguage } from "@/context/LanguageContext";

const CO = "?country=India";
const kfmt = (n) => (n >= 1000 ? `${Math.floor(n / 1000)}K+` : `${n}+`);

const TABS = [
  { key: "Institutions", base: `/explore/institutions${CO}`, qp: "&q=" },
  { key: "Courses", base: "/courses", qp: "?q=" },
  { key: "People", base: "/find-teacher", qp: "?q=" },
  { key: "Alumni", base: "/alumni-groups", qp: "?q=" },
  { key: "Scholarships", base: "/jobs", qp: "?q=" },
  { key: "Jobs", base: "/jobs", qp: "?q=" },
];

const POPULAR = [
  { label: "Engineering Colleges", to: `/explore/institutions${CO}&q=engineering` },
  { label: "MBA Colleges", to: `/explore/institutions${CO}&q=mba` },
  { label: "Medical Colleges", to: `/explore/institutions${CO}&q=medical` },
  { label: "B.Tech Courses", to: "/courses?q=b.tech" },
  { label: "Delhi University", to: `/explore/institutions${CO}&q=delhi university` },
];

const CATEGORIES = [
  { icon: Buildings, title: "Institutions", desc: "Find Schools, Colleges, Universities & Institutes", to: `/explore/institutions${CO}`, color: "text-blue-600 bg-blue-50" },
  { icon: GraduationCap, title: "Courses", desc: "Explore Courses, Programs & Specializations", to: "/disciplines", color: "text-emerald-600 bg-emerald-50" },
  { icon: UsersThree, title: "People", desc: "Find Students, Alumni, Teachers & Staff", to: "/find-teacher", color: "text-orange-600 bg-orange-50" },
  { icon: Users, title: "Alumni", desc: "Connect with Classmates, Batchmates & Alumni", to: "/alumni-groups", color: "text-violet-600 bg-violet-50" },
  { icon: Briefcase, title: "Careers", desc: "Jobs, Internships, Placements & Guidance", to: "/jobs", color: "text-sky-600 bg-sky-50" },
  { icon: UserPlus, title: "Workmate Alumni", desc: "Reconnect with Former Colleagues & Organizations", to: "/workmate", color: "text-rose-600 bg-rose-50" },
  { icon: Flower, title: "Memory", desc: "Memorials, Tributes, Archives & Memory Lane", to: "/memorials", color: "text-fuchsia-600 bg-fuchsia-50" },
  { icon: Certificate, title: "Admissions", desc: "Entrance Exams, Admissions, Dates & Requirements", to: "/exams", color: "text-teal-600 bg-teal-50" },
  { icon: Coins, title: "Scholarships", desc: "Find Scholarships, Grants & Financial Aid", to: "/scholarships", color: "text-amber-600 bg-amber-50" },
  { icon: ChatsCircle, title: "Community", desc: "Clubs, Events, Stories & Discussions", to: `/events${CO}`, color: "text-cyan-600 bg-cyan-50" },
  { icon: Newspaper, title: "Education Pulse", desc: "Latest News, Research & Education Updates", to: `/news${CO}`, color: "text-red-600 bg-red-50" },
];

const FEATURES = [
  { icon: ShieldCheck, title: "Verified Information", sub: "From Trusted Sources", tint: "text-emerald-600" },
  { icon: Brain, title: "AI-Powered Insights", sub: "Smarter Recommendations", tint: "text-blue-600" },
  { icon: Translate, title: "Multilingual Support", sub: "30+ Languages", tint: "text-violet-600" },
  { icon: Headphones, title: "Listen & Translate", sub: "Text to Speech & Translate", tint: "text-orange-600" },
  { icon: LockKey, title: "Secure & Private", sub: "Your Data, Your Control", tint: "text-emerald-700" },
];

export default function EduPulseLanding() {
  const nav = useNavigate();
  const { t } = useLanguage();
  const [tab, setTab] = useState("Institutions");
  const [q, setQ] = useState("");
  const [total, setTotal] = useState(0);

  useSeo(null, "IndianEduPulse — Discover India's education network. Explore schools, colleges and universities, find courses, alumni, teachers, classmates and workmates; preserve institutional history and build your future. Powered by AlumniPulse, part of the SocialPulse network.", null, { canonicalPath: "/" });
  useEffect(() => { api.get("/health").then((r) => setTotal(r.data?.institutions || 0)).catch(() => {}); }, []);

  const search = (e) => {
    e.preventDefault();
    const cfg = TABS.find((x) => x.key === tab) || TABS[0];
    nav(q.trim() ? `${cfg.base}${cfg.qp}${encodeURIComponent(q.trim())}` : cfg.base);
  };

  const STATS = [
    { icon: Buildings, val: total ? kfmt(total) : "50K+", label: "Institutions", sub: "Across India", color: "text-blue-600 bg-blue-50" },
    { icon: UsersThree, val: "10M+", label: "Alumni", sub: "Connected", color: "text-emerald-600 bg-emerald-50" },
    { icon: BookOpen, val: "100K+", label: "Courses", sub: "Discover", color: "text-violet-600 bg-violet-50" },
    { icon: Briefcase, val: "500K+", label: "Jobs & Internships", sub: "Opportunities", color: "text-orange-600 bg-orange-50" },
    { icon: Coins, val: "25K+", label: "Scholarships", sub: "Available", color: "text-rose-600 bg-rose-50" },
    { icon: CalendarBlank, val: "10K+", label: "Events", sub: "Every Month", color: "text-teal-600 bg-teal-50" },
  ];

  return (
    <div className="bg-background" data-testid="edupulse-landing">
      {/* Hero */}
      <section className="mx-auto grid max-w-7xl items-center gap-8 px-5 py-12 lg:grid-cols-2 lg:py-16">
        <div>
          <h1 className="font-heading text-4xl font-bold leading-[1.05] tracking-tight text-[#1e3a5f] sm:text-5xl lg:text-[3.5rem]">
            {t("India's Education Network")}
          </h1>
          <p className="mt-4 text-lg font-medium leading-snug text-muted-foreground">
            {t("Discover Institutions. Connect People.")}<br />{t("Celebrate Legacy. Build Futures.")}
          </p>

          {/* search tabs */}
          <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 border-b border-border/60" data-testid="edu-search-tabs">
            {TABS.map((x) => (
              <button key={x.key} onClick={() => setTab(x.key)} data-testid={`edu-tab-${x.key.toLowerCase()}`}
                className={`-mb-px border-b-2 pb-2 text-sm font-medium transition-colors ${tab === x.key ? "border-blue-600 text-blue-600" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                {t(x.key)}
              </button>
            ))}
          </div>

          {/* search bar */}
          <form onSubmit={search} className="mt-4 flex flex-col gap-2 rounded-2xl border border-border/70 bg-white p-2 shadow-sm sm:flex-row sm:items-center" data-testid="edu-search-form">
            <div className="flex flex-1 items-center gap-2 px-2">
              <MagnifyingGlass size={20} className="shrink-0 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Search for Schools, Colleges, Universities, Courses…")}
                className="h-10 border-0 px-0 text-base shadow-none focus-visible:ring-0" data-testid="edu-search-input" />
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-border/70 px-3 py-2 text-sm text-muted-foreground sm:border-0">
              <MapPin size={16} className="text-blue-600" /> {t("All India")} <CaretDown size={13} />
            </div>
            <Button type="submit" className="h-11 gap-1.5 rounded-xl bg-blue-600 px-6 hover:bg-blue-700" data-testid="edu-search-btn">
              <MagnifyingGlass size={17} weight="bold" /> {t("Search")}
            </Button>
          </form>

          {/* popular */}
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="edu-popular">
            <span className="text-sm font-semibold text-foreground">{t("Popular Searches")}:</span>
            {POPULAR.map((p) => (
              <Link key={p.label} to={p.to} className="rounded-full border border-border/70 bg-white px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-blue-300 hover:text-blue-600" data-testid={`edu-popular-${p.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {t(p.label)}
              </Link>
            ))}
          </div>
        </div>

        <div className="relative">
          <img src="/edupulse-hero.png" alt="India's education community" className="w-full rounded-3xl object-contain" data-testid="edu-hero-image" />
        </div>
      </section>

      {/* Stats bar */}
      <section className="mx-auto max-w-7xl px-5">
        <div className="grid grid-cols-2 gap-4 rounded-3xl border border-border/60 bg-white p-6 shadow-sm sm:grid-cols-3 lg:grid-cols-6" data-testid="edu-stats">
          {STATS.map((s) => (
            <div key={s.label} className="flex items-center gap-3" data-testid={`edu-stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${s.color}`}><s.icon size={22} weight="duotone" /></span>
              <div className="min-w-0">
                <p className="font-heading text-lg font-bold leading-none text-[#1e3a5f]">{s.val}</p>
                <p className="truncate text-xs font-medium text-foreground">{t(s.label)}</p>
                <p className="truncate text-[11px] text-muted-foreground">{t(s.sub)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Explore. Connect. Grow. */}
      <section className="mx-auto max-w-7xl px-5 py-14">
        <div className="mb-8 text-center">
          <h2 className="inline-block font-heading text-2xl font-bold text-[#1e3a5f] sm:text-3xl">{t("Explore. Connect. Grow.")}</h2>
          <div className="mx-auto mt-2 h-1 w-16 rounded-full bg-blue-600" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="edu-categories">
          {CATEGORIES.map((c) => (
            <Link key={c.title} to={c.to} data-testid={`edu-cat-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group flex items-start gap-3 rounded-2xl border border-border/60 bg-white p-5 shadow-sm transition-all hover:-translate-y-1 hover:border-blue-200 hover:shadow-md">
              <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${c.color}`}><c.icon size={22} weight="duotone" /></span>
              <div className="min-w-0">
                <h3 className="font-heading text-base font-semibold text-[#1e3a5f]">{t(c.title)}</h3>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t(c.desc)}</p>
                <CaretRight size={15} className="mt-2 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-blue-600" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Feature bar */}
      <section className="border-t border-border/60 bg-muted/20">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-5 py-8 sm:grid-cols-3 lg:grid-cols-5" data-testid="edu-features">
          {FEATURES.map((f) => (
            <div key={f.title} className="flex items-center gap-3" data-testid={`edu-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <f.icon size={26} weight="duotone" className={`${f.tint} shrink-0`} />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#1e3a5f]">{t(f.title)}</p>
                <p className="truncate text-xs text-muted-foreground">{t(f.sub)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
